import React, {
  FunctionComponent,
  useState,
  useEffect,
  useContext,
  ChangeEvent,
} from "react";
import { AppPage, CheckmarkIcon, CollapsibleBox } from "../../components";
import { Choice, ChoiceList, Button } from "@cmsgov/ds-medicare-gov";
import { CoverageType } from "../QuestionRoutingPage/types";
import { Alert } from "@cmsgov/ds-medicare-gov";
import { AppStore } from "../../app/store";
import { getCwPremiumRanges } from "../../api";
import { CwPremiumRanges, ActionType } from "../../@types";
import { $$ } from "../../helpers/currencyUtilities";
import { CONSTANTS, useQueryParams, useTranslate } from "../../helpers";
import { FormattedMessage } from "react-intl-phraseapp";
import { withRouter, RouteComponentProps } from "react-router";
import routes from "../../app/routes";

const CoverageWizardInfoPage: FunctionComponent<RouteComponentProps> = ({
  history,
  match,
  location,
}) => {
  const { state, dispatch } = useContext(AppStore.AppContext);
  const [coverageTypes, setCoverageTypes] = useState<CoverageType[]>([]);
  const [premiumRanges, setPremiumRanges] = useState<
    CwPremiumRanges | undefined
  >(undefined);
  const [noPlans, setNoPlans] = useState(false);
  const t = useTranslate();
  useQueryParams({ history, match, location });

  useEffect(() => {
    if (state.cwCoverage) {
      setCoverageTypes([state.cwCoverage]);
    }
  }, []);

  useEffect(() => {
    let covType: CoverageType | undefined;

    if (coverageTypes.length === 1) {
      covType = coverageTypes[0];
    } else if (coverageTypes.length > 1) {
      const includesPdp = coverageTypes.includes(CoverageType.PDP);
      const includesMedigap = coverageTypes.includes(
        CoverageType.MedigapPolicy
      );

      if (includesPdp && includesMedigap) {
        covType = CoverageType.PDPAndMedigap;
      } else if (includesPdp) {
        covType = CoverageType.PDP;
      } else if (includesMedigap) {
        covType = CoverageType.MedigapPolicy;
      }
    }

    dispatch({ type: ActionType.UPDATE_CW_COVERAGE, payload: covType });
  }, [coverageTypes]);

  useEffect(() => {
    const { zipcode, fips, year } = state;

    if (zipcode && fips && year) {
      getCwPremiumRanges({ zipcode, fips, year }).then(
        (res): void => {
          setPremiumRanges(res);

          if (res) {
            const notNull = Object.keys(res)
              .filter(k => k !== "request_id")
              .map(k => res[k])
              .filter(v => v !== null);

            setNoPlans(notNull.length === 0);
          }
        },
        (): void => setNoPlans(true)
      );
    }
  }, [state.zipcode, state.fips, state.year]);

  const CoverageIncludes: FunctionComponent<{ coverageType: CoverageType }> = ({
    coverageType,
  }) => {
    const includedInCoverage: {
      [key: string]: {
        partA: boolean;
        partB: boolean;
        drugs: boolean;
        oopHelp: boolean;
        anyDoctor: boolean;
        vision: boolean;
      };
    } = {
      [CoverageType.OriginalMedicare]: {
        partA: true,
        partB: true,
        drugs: false,
        oopHelp: false,
        anyDoctor: true,
        vision: false,
      },
      [CoverageType.MedigapPolicy]: {
        partA: true,
        partB: true,
        drugs: false,
        oopHelp: true,
        anyDoctor: true,
        vision: false,
      },
      [CoverageType.PDP]: {
        partA: true,
        partB: true,
        drugs: true,
        oopHelp: false,
        anyDoctor: true,
        vision: false,
      },
      [CoverageType.PDPAndMedigap]: {
        partA: true,
        partB: true,
        drugs: true,
        oopHelp: true,
        anyDoctor: true,
        vision: false,
      },
      [CoverageType.MAPD]: {
        partA: true,
        partB: true,
        drugs: true,
        oopHelp: true,
        anyDoctor: false,
        vision: true,
      },
    };

    const coverage = includedInCoverage[coverageType];

    return (
      <div className="ds-u-border-bottom--1 ds-u-padding-bottom--3">
        <p>{t("cw.info.coverage_includes")}:</p>
        <ul className="ds-c-list ds-c-list--bare" role="list">
          <li aria-hidden={!coverage.partA}>
            <CheckmarkIcon checked={coverage.partA} /> {t("cw.info.part_a")}
          </li>
          <li aria-hidden={!coverage.partB}>
            <CheckmarkIcon checked={coverage.partB} /> {t("cw.info.part_b")}
          </li>
          <li aria-hidden={!coverage.drugs}>
            <CheckmarkIcon checked={coverage.drugs} /> {t("cw.info.drugs")}
          </li>
          <li aria-hidden={!coverage.oopHelp}>
            <CheckmarkIcon checked={coverage.oopHelp} />{" "}
            {t("cw.info.oop_costs")}
          </li>
          <li aria-hidden={!coverage.anyDoctor}>
            <CheckmarkIcon checked={coverage.anyDoctor} />{" "}
            {t("cw.info.any_doctor")}
          </li>
          <li aria-hidden={!coverage.vision}>
            <CheckmarkIcon checked={coverage.vision} />{" "}
            {t("cw.info.vision_hearing")}
          </li>
        </ul>
      </div>
    );
  };

  const Explainer: FunctionComponent<{ label: string; open: boolean }> = ({
    open,
    label,
    children,
  }) => {
    return (
      <div className="ds-u-fill--gray-lightest ds-u-padding--3 ds-u-margin-bottom--3">
        <CollapsibleBox label={<h4>{label}</h4>} open={open}>
          {children}
        </CollapsibleBox>
      </div>
    );
  };

  const OriginalMedicareExplainer: FunctionComponent<{ open: boolean }> = ({
    open,
  }) => (
    <Explainer label={t("cw.info.om")} open={open}>
      <p className="ds-u-border-bottom--1  ds-u-padding-bottom--2">
        {t("cw.info.om.part_a_premium")}:{" "}
        <span className="ds-u-font-weight--bold">
          {t("cw.info.om.usually_free")}
        </span>
        <br />
        {t("cw.info.om.standard_part_b_premium")}:{" "}
        <span className="ds-u-font-weight--bold">
          {state.year && $$(CONSTANTS.partBPremium[state.year])}
        </span>
      </p>
      <ul className="ds-c-list">
        <li>{t("cw.info.om.covers_80")}</li>
        <li>{t("cw.info.om.remaining_20")}</li>
        <li>{t("cw.info.om.medigap")}</li>
        <li>{t("cw.info.om.part_d")}</li>
        <li>{t("cw.info.om.doctor")}</li>
      </ul>
      <p>
        <FormattedMessage
          id="cw.info.om.learn_more"
          values={{
            learnMoreAboutSavings: (
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.medicare.gov/your-medicare-costs/get-help-paying-costs"
              >
                {t("cw.info.om.savings_programs")}
              </a>
            ),
            infoAboutOriginalMedicare: (
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.medicare.gov/what-medicare-covers/your-medicare-coverage-choices/how-original-medicare-works"
              >
                {t("cw.info.om.general_info")}
              </a>
            ),
          }}
        />
      </p>
      <Alert hideIcon={true} variation="warn" className="ds-u-padding-y--1">
        <p>{t("cw.info.om.enroll_late")}</p>
        <p>{t("cw.info.om.first_eligible")}</p>
      </Alert>
    </Explainer>
  );

  const getTotalMonthlyPreimum = (): string => {
    let low = 0;
    let high = 0;

    if (noPlans) {
      return t("cw.info.not_available");
    }

    if (premiumRanges) {
      if (coverageTypes.includes(CoverageType.MedigapPolicy)) {
        const { medigap_premium_min, medigap_premium_max } = premiumRanges;

        low += medigap_premium_min !== null ? medigap_premium_min : 0;
        high += medigap_premium_max !== null ? medigap_premium_max : 0;
      }

      if (coverageTypes.includes(CoverageType.PDP)) {
        const { pdp_premium_min, pdp_premium_max } = premiumRanges;

        low += pdp_premium_min !== null ? pdp_premium_min : 0;
        high += pdp_premium_max !== null ? pdp_premium_max : 0;
      }

      if (coverageTypes.includes(CoverageType.MAPD)) {
        const { ma_premium_min, ma_premium_max } = premiumRanges;

        low += ma_premium_min !== null ? ma_premium_min : 0;
        high += ma_premium_max !== null ? ma_premium_max : 0;
      }

      if (coverageTypes.includes(CoverageType.OriginalMedicare) && state.year) {
        low += CONSTANTS.partBPremium[state.year];
        high += CONSTANTS.partBPremium[state.year];
      }
    }

    return low == high ? $$(low) : `${$$(low)}-${$$(high)}`;
  };

  const getPremiumRange = (type: CoverageType): string => {
    if (premiumRanges) {
      if (type === CoverageType.MedigapPolicy) {
        const { medigap_premium_min, medigap_premium_max } = premiumRanges;

        if (medigap_premium_min !== null) {
          let premium = `${$$(medigap_premium_min)}`;

          if (medigap_premium_max !== null) {
            premium =
              medigap_premium_min === medigap_premium_max
                ? `${$$(medigap_premium_min)}`
                : `${$$(medigap_premium_min)}-${$$(medigap_premium_max)}`;
          }

          return premium;
        }
      } else if (type === CoverageType.PDP) {
        const { pdp_premium_min, pdp_premium_max } = premiumRanges;

        if (pdp_premium_min !== null) {
          let premium = `${$$(pdp_premium_min)}`;

          if (pdp_premium_max !== null) {
            premium =
              pdp_premium_min === pdp_premium_max
                ? `${$$(pdp_premium_min)}`
                : `${$$(pdp_premium_min)}-${$$(pdp_premium_max)}`;
          }

          return premium;
        }
      } else if (type === CoverageType.MAPD) {
        const { ma_premium_min, ma_premium_max } = premiumRanges;

        if (ma_premium_min !== null) {
          let premium = `${$$(ma_premium_min)}`;

          if (ma_premium_max !== null) {
            premium =
              ma_premium_min === ma_premium_max
                ? `${$$(ma_premium_min)}`
                : `${$$(ma_premium_min)}-${$$(ma_premium_max)}`;
          }

          return premium;
        }
      }
    }

    return t("cw.info.no_plans_available");
  };

  const MedigapPolicyExplainer: FunctionComponent<{ open: boolean }> = ({
    open,
  }) => (
    <Explainer label={t("cw.info.medigap_policy")} open={open}>
      <p className="ds-u-border-bottom--1 ds-u-padding-bottom--2">
        {t("cw.info.medigap_policy.premium_range")} {state.zipcode}:{" "}
        <span className="ds-u-font-weight--bold">
          {getPremiumRange(CoverageType.MedigapPolicy)}
        </span>
      </p>
      <ul className="ds-c-list">
        <li>{t("cw.info.medigap_policy.pays_20")}</li>
        <li>{t("cw.info.medigap_policy.never_drop")}</li>
        <li>{t("cw.info.medigap_policy.reflect")}</li>
      </ul>
      <p>
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.medicare.gov/supplements-other-insurance/whats-medicare-supplement-insurance-medigap"
        >
          {t("cw.info.medigap_policy.learn_more")}
        </a>
      </p>
      <Alert hideIcon={true} variation="warn" className="ds-u-padding-y--1">
        <p>{t("cw.info.medigap_policy.enroll_late")}</p>
        <p>{t("cw.info.medigap_policy.first_eligible")}</p>
      </Alert>
    </Explainer>
  );

  const PartDExplainer: FunctionComponent<{ open: boolean }> = ({ open }) => (
    <Explainer label={t("cw.info.drug_plan")} open={open}>
      <p className="ds-u-border-bottom--1 ds-u-padding-bottom--2">
        {t("cw.info.drug_plan.premium_range")} {state.zipcode}:{" "}
        <span className="ds-u-font-weight--bold">
          {getPremiumRange(CoverageType.PDP)}
        </span>
      </p>
      <ul className="ds-c-list">
        <li>{t("cw.info.drug_plan.separate_drug_coverage")}</li>
        <li>{t("cw.info.drug_plan.switch")}</li>
        <li>{t("cw.info.drug_plan.vary_cost")}</li>
        <li>{t("cw.info.drug_plan.plan_pricing")}</li>
      </ul>
      <p>
        <FormattedMessage
          id="cw.info.drug_plan.learn_more"
          values={{
            programsThatReduceCosts: (
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.medicare.gov/your-medicare-costs/get-help-paying-costs/lower-prescription-costs"
              >
                {t("cw.info.drug_plan.reduce_costs")}
              </a>
            ),
            drugPlanInfo: (
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.medicare.gov/drug-coverage-part-d"
              >
                {t("cw.info.drug_plan.info_drug_plans")}
              </a>
            ),
          }}
        />
      </p>
      <Alert hideIcon={true} variation="warn" className="ds-u-padding-y--1">
        <p>{t("cw.info.drug_plan.enroll_late")}</p>
        <p>{t("cw.info.drug_plan.first_eligible")}</p>
      </Alert>
    </Explainer>
  );

  const MedicareAdvantageExplainer: FunctionComponent<{ open: boolean }> = ({
    open,
  }) => (
    <Explainer label={t("cw.info.mapd")} open={open}>
      <p className="ds-u-border-bottom--1 ds-u-padding-bottom--2">
        {t("cw.info.mapd.premium_range")} {state.zipcode}:{" "}
        <span className="ds-u-font-weight--bold">
          {getPremiumRange(CoverageType.MAPD)}
        </span>
      </p>
      <ul className="ds-c-list">
        <li>{t("cw.info.mapd.part_a_b")}</li>
        <li>{t("cw.info.mapd.doctors")}</li>
        <li>{t("cw.info.mapd.drug_coverage")}</li>
        <li>{t("cw.info.mapd.extra_benefits")}</li>
        <li>{t("cw.info.mapd.oop_costs")}</li>
      </ul>
      <p>
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.medicare.gov/sign-up-change-plans/types-of-medicare-health-plans/medicare-advantage-plans/how-do-medicare-advantage-plans-work"
        >
          {t("cw.info.mapd.learn_more")}
        </a>
      </p>
      <Alert hideIcon={true} variation="warn" className="ds-u-padding-y--1">
        <p>{t("cw.info.mapd.eligible")}</p>
      </Alert>
    </Explainer>
  );

  const coverageTypeLabel = {
    [CoverageType.MAPD]: t("cw.info.types.mapd"),
    [CoverageType.PDP]: t("cw.info.types.pdp"),
    [CoverageType.MedigapPolicy]: t("cw.info.types.medigap_policy"),
    [CoverageType.PDPAndMedigap]: t("cw.info.types.pdp_medigap"),
    [CoverageType.OriginalMedicare]: t("cw.info.types.original_medicare"),
  };

  const getExplainer = (params: {
    open: boolean;
    type: CoverageType;
  }): JSX.Element => {
    const { type, open } = params;

    if (type === CoverageType.MAPD) {
      return <MedicareAdvantageExplainer open={open} />;
    } else if (type === CoverageType.PDP) {
      return <PartDExplainer open={open} />;
    } else if (type === CoverageType.OriginalMedicare) {
      return <OriginalMedicareExplainer open={open} />;
    } else if (type === CoverageType.MedigapPolicy) {
      return <MedigapPolicyExplainer open={open} />;
    }

    return <></>;
  };

  return (
    <AppPage
      heading={t("cw.info.your_options")}
      subHeading={t("cw.info.click_option")}
    >
      <div>
        <div className="CoverageWizardInfoPage__options">
          <div className="CoverageWizardInfoPage__optionContainer CoverageWizardInfoPage__optionContainer--bordered ds-u-padding-right--4">
            <Choice
              type="radio"
              value="om"
              name="coverage-type"
              checked={coverageTypes.includes(CoverageType.OriginalMedicare)}
              onChange={(): void => {
                if (!coverageTypes.includes(CoverageType.OriginalMedicare)) {
                  setCoverageTypes([CoverageType.OriginalMedicare]);
                }
              }}
            >
              <h2 className="ds-h4">{t("cw.info.types.original_medicare")}</h2>
            </Choice>
            <ChoiceList
              choices={[
                {
                  label: t("cw.info.medigap"),
                  value: CoverageType.MedigapPolicy,
                  disabled: coverageTypes.includes(CoverageType.MAPD),
                  checked: coverageTypes.includes(CoverageType.MedigapPolicy),
                },
                {
                  label: t("cw.info.drug_plan"),
                  value: CoverageType.PDP,
                  disabled: coverageTypes.includes(CoverageType.MAPD),
                  checked: coverageTypes.includes(CoverageType.PDP),
                },
              ]}
              onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                const { value } = e.target;
                const newCovType = value as CoverageType;

                if (!coverageTypes.includes(newCovType)) {
                  setCoverageTypes([...coverageTypes, newCovType]);
                } else {
                  setCoverageTypes(
                    coverageTypes.filter(ct => ct !== newCovType)
                  );
                }
              }}
              className="ds-u-margin-top--0 ds-u-margin-left--5"
              labelClassName="ds-h5"
              label={t("cw.info.extra_coverage")}
              name="checkbox_choices"
              type="checkbox"
            />
          </div>
          <div className="CoverageWizardInfoPage__optionContainer ds-u-md-padding-left--4">
            <Choice
              type="radio"
              value="mapd"
              name="coverage-type"
              checked={coverageTypes.includes(CoverageType.MAPD)}
              onChange={(): void => {
                if (!coverageTypes.includes(CoverageType.MAPD)) {
                  setCoverageTypes([CoverageType.MAPD]);
                }
              }}
            >
              <h2 className="ds-h4">{t("cw.info.mapd_plans")}</h2>
            </Choice>
          </div>
        </div>
        <div className="CoverageWizardInfoPage__coverageInfo">
          <h2>{state.cwCoverage && coverageTypeLabel[state.cwCoverage]}</h2>
          <div className="ds-u-border-top--1">
            {noPlans && (
              <Alert variation="error" className="ds-u-margin-top--3">
                {t("cw.info.no_plans")}
              </Alert>
            )}
            <div className="ds-u-md-display--flex ">
              <div className="CoverageWizardInfoPage__coverageBlock CoverageWizardInfoPage__coverageIncludes">
                {state.cwCoverage && (
                  <CoverageIncludes coverageType={state.cwCoverage} />
                )}

                {(coverageTypes.includes(CoverageType.OriginalMedicare) ||
                  coverageTypes.includes(CoverageType.PDP) ||
                  coverageTypes.includes(CoverageType.MedigapPolicy)) && (
                  <div className="ds-u-border-bottom--1">
                    {coverageTypes.includes(CoverageType.OriginalMedicare) && (
                      <p>
                        {t("cw.info.standard_part_b_premium")}:{" "}
                        {state.year && $$(CONSTANTS.partBPremium[state.year])}
                      </p>
                    )}

                    {coverageTypes.includes(CoverageType.MedigapPolicy) && (
                      <p>
                        {t("cw.info.medigap_range")}:{" "}
                        {getPremiumRange(CoverageType.MedigapPolicy)}
                      </p>
                    )}

                    {coverageTypes.includes(CoverageType.PDP) && (
                      <p>
                        {t("cw.info.drug_range")}:{" "}
                        {getPremiumRange(CoverageType.PDP)}
                      </p>
                    )}
                  </div>
                )}

                <p className="ds-u-margin-bottom--0">
                  {t("cw.info.total_monthly")}
                </p>
                <h3 className="ds-u-margin--0">{getTotalMonthlyPreimum()}</h3>
              </div>
              <div className="CoverageWizardInfoPage__coverageBlock CoverageWizardInfoPage__coverageExplained">
                {[...coverageTypes]
                  .reverse()
                  .map((type, i) => getExplainer({ open: i === 0, type }))}
              </div>
            </div>
          </div>
        </div>
        <div>
          <h3>{t("cw.info.ready")}</h3>
          <p>
            <FormattedMessage
              id="cw.info.learn_more"
              values={{
                learnMoreLink: (
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://www.medicare.gov/sign-up-change-plans/joining-a-health-or-drug-plan"
                  >
                    {t("cw.info.join")}
                  </a>
                ),
                seeWhatMedicareCovers: (
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://www.medicare.gov/what-medicare-covers"
                  >
                    {t("cw.info.see_what_medicare_covers")}
                  </a>
                ),
              }}
            />
          </p>
          <p>
            {state.cwCoverage && (
              <FormattedMessage
                id="cw.info.you_chose"
                values={{
                  planType: (
                    <span className="ds-u-font-weight--bold">
                      {coverageTypeLabel[state.cwCoverage]}
                    </span>
                  ),
                }}
              />
            )}
            <Button
              variation="transparent"
              className="ds-u-padding--0 ds-u-margin--0 ds-u-margin-left--1 ds-u-display--inline-block"
              onClick={(): void => window.scrollTo(0, 0)}
            >
              {t("cw.info.change_selection")}
            </Button>
          </p>

          <Button
            size="big"
            variation="primary"
            onClick={(): void =>
              history.push(
                `${routes.questionRouting}?zip=${state.zipcode}&fips=${state.fips}`
              )
            }
          >
            {t("cw.info.look_plans")}
          </Button>
        </div>
      </div>
    </AppPage>
  );
};

export default withRouter(CoverageWizardInfoPage);
